import React, { useState } from 'react'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const {
  FiUsers,
  FiCheck,
  FiX,
  FiClipboard,
  FiBookOpen,
  FiShare2,
  FiFolderPlus
} = FiIcons

const TeacherHub = () => {
  const [linkRequests, setLinkRequests] = useState([
    { id: 1, student: 'Eli Johnson', parent: 'Maria Johnson', note: 'Math tutoring support' },
    { id: 2, student: 'Sofia Lee', parent: 'Kevin Lee', note: 'Weekly check-ins requested' }
  ])
  const [library, setLibrary] = useState([
    { id: 1, title: 'STEM Stations - Grade 4', items: 8 },
    { id: 2, title: 'Writing Conferences Toolkit', items: 5 },
    { id: 3, title: 'Bible Study Warmups', items: 6 }
  ])
  const [newLibraryItem, setNewLibraryItem] = useState('')
  const [students] = useState([
    { id: 1, name: 'Lena Carter', progress: 82 },
    { id: 2, name: 'Malik Rivera', progress: 68 },
    { id: 3, name: 'Priya Desai', progress: 75 }
  ])

  const handleRequest = (id) => {
    setLinkRequests((prev) => prev.filter((req) => req.id !== id))
  }

  const addLibraryItem = (event) => {
    event.preventDefault()
    if (!newLibraryItem.trim()) return
    setLibrary((prev) => [
      ...prev,
      { id: prev.length + 1, title: newLibraryItem, items: 0 }
    ])
    setNewLibraryItem('')
  }

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-[#1B4C87] to-[#00AAFF] rounded-2xl p-8 text-white">
        <h1 className="text-3xl font-bold mb-3">Teacher Dashboard</h1>
        <p className="text-blue-50 max-w-3xl">
          Manage linked students, handle parent requests, monitor progress, and share curriculum
          resources in one place.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Linked Students */}
        <div className="bg-white rounded-xl shadow-lg p-6 lg:col-span-2 space-y-4">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiUsers} className="w-5 h-5 text-[#00AAFF]" />
            <h2 className="text-xl font-bold text-gray-800">Linked Students</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {students.map((student) => (
              <div key={student.id} className="p-3 rounded-lg border border-gray-200 bg-gray-50">
                <p className="font-semibold text-gray-800">{student.name}</p>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="h-2 rounded-full bg-gradient-to-r from-[#00AAFF] to-[#1B4C87]"
                    style={{ width: `${student.progress}%` }}
                  />
                </div>
                <p className="text-xs text-gray-600 mt-1">Progress: {student.progress}%</p>
              </div>
            ))}
          </div>
        </div>

        {/* Link Requests */}
        <div className="bg-white rounded-xl shadow-lg p-6 space-y-3">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiClipboard} className="w-5 h-5 text-[#1B4C87]" />
            <h3 className="text-lg font-bold text-gray-800">Link Requests</h3>
          </div>
          {linkRequests.length === 0 ? (
            <p className="text-sm text-gray-600">All caught up! No pending requests.</p>
          ) : (
            linkRequests.map((request) => (
              <div key={request.id} className="p-3 rounded-lg border border-gray-200 bg-gray-50">
                <p className="font-semibold text-gray-800">{request.student}</p>
                <p className="text-sm text-gray-600">Parent: {request.parent}</p>
                <p className="text-sm text-gray-600">Note: {request.note}</p>
                <div className="flex items-center gap-2 mt-3">
                  <button
                    onClick={() => handleRequest(request.id)}
                    className="flex items-center gap-1 px-3 py-1 rounded-lg bg-green-100 text-green-700"
                  >
                    <SafeIcon icon={FiCheck} className="w-4 h-4" />
                    Accept
                  </button>
                  <button
                    onClick={() => handleRequest(request.id)}
                    className="flex items-center gap-1 px-3 py-1 rounded-lg bg-red-100 text-red-700"
                  >
                    <SafeIcon icon={FiX} className="w-4 h-4" />
                    Decline
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bulk Progress */}
        <div className="bg-white rounded-xl shadow-lg p-6 lg:col-span-2 space-y-3">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiBookOpen} className="w-5 h-5 text-[#00AAFF]" />
            <h3 className="text-lg font-bold text-gray-800">Bulk Progress View</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {students.map((student) => (
              <div key={`bulk-${student.id}`} className="p-3 rounded-lg border border-gray-200 bg-gray-50">
                <p className="font-semibold text-gray-800">{student.name}</p>
                <p className="text-sm text-gray-600">Math / ELA / Science</p>
                <p className="text-sm text-gray-700">Progress snapshot: {student.progress}%</p>
              </div>
            ))}
          </div>
          <div className="text-sm text-gray-600 bg-[#00AAFF]/10 p-3 rounded-lg">
            Export-ready summaries help teachers share updates with administrators and parents.
          </div>
        </div>

        {/* Shared Library */}
        <div className="bg-white rounded-xl shadow-lg p-6 space-y-3">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiShare2} className="w-5 h-5 text-[#1B4C87]" />
            <h3 className="text-lg font-bold text-gray-800">Shared Curriculum Library</h3>
          </div>
          <ul className="space-y-2 text-sm text-gray-700">
            {library.map((item) => (
              <li key={item.id} className="p-2 bg-gray-50 rounded-lg border border-gray-200">
                <p className="font-semibold text-gray-800">{item.title}</p>
                <p className="text-xs text-gray-600">{item.items} resources</p>
              </li>
            ))}
          </ul>
          <form className="flex items-center gap-2" onSubmit={addLibraryItem}>
            <input
              className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm"
              placeholder="Add collection (e.g., Project Rubrics)"
              value={newLibraryItem}
              onChange={(e) => setNewLibraryItem(e.target.value)}
            />
            <button
              type="submit"
              className="p-2 rounded-lg bg-[#00AAFF] text-white hover:bg-[#0095E0]"
            >
              <SafeIcon icon={FiFolderPlus} className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}

export default TeacherHub
